import React, { useRef, useState } from 'react'
import { makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import classNames from 'classnames'
import { useDrag, useDrop } from 'react-dnd'
import type { User } from '@paintscout/api'

const useStyles = makeStyles<Theme, CrewLeftColumnItemProps>(
  (theme) => ({
    root: {
      borderTop: '1px solid #f1f5f9', // border-t border-slate-100
      backgroundColor: 'rgba(248, 250, 252, 0.3)', // bg-slate-50/30
      position: 'relative' // relative
    },
    crewDot: {
      width: '0.75rem', // w-3
      height: '0.75rem', // h-3
      borderRadius: '9999px', // rounded-full
      boxShadow: theme.shadows[1], // shadow-sm
      flexShrink: 0, // flex-shrink-0
      marginTop: '3px' // mt-[3px]
    },
    crewDotNarrow: {
      cursor: 'pointer',
      marginTop: '1.25rem',
      marginLeft: 'auto',
      marginRight: 'auto',
      // Mobile: pill-shaped instead of a small circular dot.
      [theme.breakpoints.down('xs')]: {
        width: '1.5rem',
        height: '0.625rem',
        borderRadius: '999px'
      }
    },
    crewDetails: {
      flex: 1,
      minWidth: 0
    },
    crewName: {
      fontSize: '0.75rem', // text-xs
      color: '#475569', // text-[#475569]
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' // truncate
    },
    container: {
      display: 'flex', // flex
      alignItems: 'flex-start' // items-start
    },
    smallContainer: {
      paddingLeft: 0,
      paddingRight: 0,
      justifyContent: 'center',
      gap: '0.375rem' // 1.5 * 0.25rem = 0.375rem
    },
    largeContainer: {
      padding: '0.75rem', // p-3
      gap: '0.75rem' // gap-3
    },
    tooltipDetailsContainer: {
      position: 'absolute', // absolute
      left: '100%', // left-full
      marginLeft: '0.5rem', // ml-2
      top: '1rem', // top-4
      backgroundColor: '#1e293b', // bg-[#1e293b]
      color: '#fff', // text-white
      paddingLeft: '0.5rem', // pl-2
      paddingRight: '0.375rem', // pr-1.5
      paddingTop: '0.25rem', // py-1 (top)
      paddingBottom: '0.25rem', // py-1 (bottom)
      borderRadius: '0.375rem', // rounded
      fontSize: '0.75rem', // text-xs
      whiteSpace: 'nowrap', // whitespace-nowrap
      zIndex: 50, // z-50
      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)' // shadow-lg
    },
    tooltipArrow: {
      position: 'absolute', // absolute
      right: '100%', // right-full
      top: '50%', // top-1/2
      transform: 'translateY(-50%)', // -translate-y-1/2
      borderWidth: '4px', // border-4
      borderStyle: 'solid',
      borderColor: 'transparent', // border-transparent
      borderRightColor: '#1e293b' // border-r-[#1e293b]
    }
  }),
  { name: 'CrewLeftColumnItem' }
)

export interface CrewLeftColumnItemProps {
  classes?: StyleClasses<typeof useStyles>
  displayCrew: {
    id: string
    name: string
    color: string
    siteLeadName?: string
  }
  minHeight: number
  leftColumnWidth: number
  projectManager: User
  onMoveLead: (draggedLeadId: string, targetLeadId: string) => void
}

function CrewLeftColumnItem(props: CrewLeftColumnItemProps) {
  const classes = useStyles(props)
  const { displayCrew, minHeight, leftColumnWidth, projectManager, onMoveLead } = props
  const [showCrewTooltip, setShowCrewTooltip] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const [{ isDragging: _isDragging }, _, preview] = useDrag({
    type: 'LEAD',
    item: { projectManagerId: projectManager.user_id },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  const [{ isOver: _isOver }, drop] = useDrop({
    accept: 'LEAD',
    drop: (item: { projectManagerId: string }) => {
      if (item.projectManagerId !== projectManager.user_id) {
        onMoveLead(item.projectManagerId, projectManager.user_id)
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver()
    })
  })
  preview(drop(ref))

  return (
    <div className={classes.root} ref={ref} style={{ height: `${minHeight}px` }}>
      <div
        className={classNames({
          [classes.container]: true,
          [classes.smallContainer]: leftColumnWidth < 150,
          [classes.largeContainer]: leftColumnWidth >= 150
        })}
      >
        {/* Narrow View: just the colored dot, with hover tooltip for the crew name */}
        {leftColumnWidth < 150 ? (
          <div
            className={classNames(classes.crewDot, classes.crewDotNarrow)}
            style={{ backgroundColor: displayCrew.color }}
            onMouseEnter={() => {
              setShowCrewTooltip(true)
              setTimeout(() => setShowCrewTooltip(false), 2000)
            }}
            onClick={() => {
              setShowCrewTooltip(true)
              setTimeout(() => setShowCrewTooltip(false), 2000)
            }}
          />
        ) : (
          <>
            {/* Wide View: small colored dot + crew name */}
            <div className={classes.crewDot} style={{ backgroundColor: displayCrew.color }} />
            <div className={classes.crewDetails}>
              <div className={classes.crewName}>
                <Typography variant={'body1'}>{displayCrew.name}</Typography>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Mobile Crew Tooltip */}
      {showCrewTooltip && leftColumnWidth < 150 && (
        <div className={classes.tooltipDetailsContainer}>
          {displayCrew.name}
          <div className={classes.tooltipArrow}></div>
        </div>
      )}
    </div>
  )
}

export default CrewLeftColumnItem
